/**
 * AutoComplete — Type-ahead autocomplete component for Parts Finder
 *
 * Usage:
 *   new AutoComplete({
 *     input: document.getElementById('my-input'),
 *     items: ['BOBCAT', 'CAT', 'CATERPILLAR', ...],
 *     onSelect: (value, label) => { ... },
 *     placeholder: 'Type manufacturer...',
 *     maxVisible: 8
 *   });
 *
 * Config options:
 *   input        — DOM element (input[type=text]) or CSS selector string
 *   items        — Array of strings OR array of { value, label } objects
 *   onSelect     — Callback when an item is selected: (value, label) => void
 *   placeholder  — Input placeholder text (default: 'Type to search...')
 *   maxVisible   — Max dropdown items visible before scroll (default: 8)
 *   minChars     — Min characters before filtering (default: 0, shows all on focus)
 *   disabled     — Start in disabled state (default: false)
 *   theme        — 'dark' or 'light' (default: 'dark')
 *   name         — Instance name for debugging (default: 'autocomplete')
 */
(function(root) {
  'use strict';

  // ── Static filter function (exported for testing) ──
  function filterItems(items, query, maxVisible) {
    if (!query || query.trim() === '') return items.slice(0, maxVisible || items.length);

    const q = query.toLowerCase().trim();
    const startMatches = [];
    const containsMatches = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const label = (typeof item === 'string' ? item : item.label).toLowerCase();

      if (label.indexOf(q) === 0) {
        startMatches.push(item);
      } else if (label.indexOf(q) > 0) {
        containsMatches.push(item);
      }
    }

    // Start-of-string matches first, then contains matches
    const combined = startMatches.concat(containsMatches);
    return maxVisible ? combined.slice(0, maxVisible) : combined;
  }

  // ── Highlight matching text ──
  function highlightMatch(text, query) {
    if (!query) return escHtml(text);
    const q = query.trim();
    if (!q) return escHtml(text);

    const lowerText = text.toLowerCase();
    const lowerQ = q.toLowerCase();
    const idx = lowerText.indexOf(lowerQ);

    if (idx === -1) return escHtml(text);

    const before = text.substring(0, idx);
    const match = text.substring(idx, idx + q.length);
    const after = text.substring(idx + q.length);

    return escHtml(before) + '<strong>' + escHtml(match) + '</strong>' + escHtml(after);
  }

  function escHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── AutoComplete class ──
  function AutoComplete(config) {
    if (!(this instanceof AutoComplete)) return new AutoComplete(config);

    this.config = Object.assign({
      placeholder: 'Type to search...',
      maxVisible: 8,
      minChars: 0,
      disabled: false,
      theme: 'dark',
      name: 'autocomplete'
    }, config);

    // Resolve input element
    this.inputEl = typeof this.config.input === 'string'
      ? document.querySelector(this.config.input)
      : this.config.input;

    if (!this.inputEl) {
      console.warn('AutoComplete: input element not found for', this.config.name);
      return;
    }

    this._items = [];
    this._filteredItems = [];
    this._highlightIndex = -1;
    this._isOpen = false;
    this._disabled = this.config.disabled;
    this._selectedValue = null;
    this._selectedLabel = null;
    this._debounceTimer = null;

    this._buildDOM();
    this._bindEvents();
    this.setItems(this.config.items || []);

    if (this._disabled) this.disable();
  }

  AutoComplete.prototype._buildDOM = function() {
    // Wrap the input in a container
    this._wrapper = document.createElement('div');
    this._wrapper.className = 'ac-wrapper ac-theme-' + this.config.theme;
    this._wrapper.setAttribute('data-ac-name', this.config.name);

    // Replace the original input's parent position
    this.inputEl.parentNode.insertBefore(this._wrapper, this.inputEl);
    this._wrapper.appendChild(this.inputEl);

    // Style the input
    this.inputEl.classList.add('ac-input');
    this.inputEl.setAttribute('placeholder', this.config.placeholder);
    this.inputEl.setAttribute('autocomplete', 'off');
    this.inputEl.setAttribute('autocorrect', 'off');
    this.inputEl.setAttribute('autocapitalize', 'off');
    this.inputEl.setAttribute('spellcheck', 'false');
    this.inputEl.setAttribute('role', 'combobox');
    this.inputEl.setAttribute('aria-autocomplete', 'list');
    this.inputEl.setAttribute('aria-expanded', 'false');

    // Clear button
    this._clearBtn = document.createElement('button');
    this._clearBtn.className = 'ac-clear';
    this._clearBtn.setAttribute('type', 'button');
    this._clearBtn.setAttribute('aria-label', 'Clear');
    this._clearBtn.setAttribute('tabindex', '-1');
    this._clearBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3L11 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    this._clearBtn.style.display = 'none';
    this._wrapper.appendChild(this._clearBtn);

    // Dropdown
    this._dropdown = document.createElement('div');
    this._dropdown.className = 'ac-dropdown';
    this._dropdown.setAttribute('role', 'listbox');
    this._dropdown.style.display = 'none';
    this._wrapper.appendChild(this._dropdown);
  };

  AutoComplete.prototype._bindEvents = function() {
    var self = this;

    // Input events
    this.inputEl.addEventListener('input', function() {
      clearTimeout(self._debounceTimer);
      self._debounceTimer = setTimeout(function() {
        self._onInput();
      }, 150);
    });

    this.inputEl.addEventListener('focus', function() {
      if (!self._disabled && self._items.length > 0) {
        // If there's a selected value and input shows the label, clear for searching
        if (self._selectedValue && self.inputEl.value === self._selectedLabel) {
          self.inputEl.value = '';
        }
        // Record when this focus-open happened. The click that accompanies the
        // same tap is swallowed by the click handler via a short time window.
        // Timing-robust: avoids the macrotask race where a setTimeout-cleared
        // flag could fire BEFORE the click and let it toggle the dropdown shut.
        self._focusOpenAt = Date.now();
        self._onInput();
      }
    });

    // Tap/click the input again to TOGGLE the dropdown closed (previously it
    // could only be dismissed by tapping outside the field).
    this.inputEl.addEventListener('click', function() {
      if (self._disabled) return;
      // The first tap of an unfocused input fires focus (which opened it)
      // immediately before this click — swallow that accompanying click. Using a
      // short time window instead of a one-shot flag avoids a macrotask race.
      if (Date.now() - (self._focusOpenAt || 0) < 350) {
        return;
      }
      if (self._isOpen) {
        self._close();
        self.inputEl.blur();
      } else if (self._items.length > 0) {
        self._filter(self.inputEl.value);
        self._open();
      }
    });

    this.inputEl.addEventListener('keydown', function(e) {
      self._onKeydown(e);
    });

    // Clear button
    this._clearBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      self.clear();
      self.inputEl.focus();
    });

    // Click outside to close
    document.addEventListener('click', function(e) {
      if (!self._wrapper.contains(e.target)) {
        self._close();
      }
    });

    // Touch support — prevent zoom on mobile
    this.inputEl.addEventListener('touchstart', function() {
      // Just ensure focus happens
    }, { passive: true });
  };

  AutoComplete.prototype._onInput = function() {
    var query = this.inputEl.value;
    this._selectedValue = null; // User is typing, clear selection

    if (query.length < this.config.minChars && query.length > 0) {
      this._close();
      return;
    }

    this._filter(query);
    this._open();
    this._updateClearButton();
  };

  AutoComplete.prototype._onKeydown = function(e) {
    if (this._disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this._isOpen) {
          this._filter(this.inputEl.value);
          this._open();
        } else {
          this._moveHighlight(1);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (this._isOpen) {
          this._moveHighlight(-1);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (this._isOpen && this._highlightIndex >= 0) {
          this._selectIndex(this._highlightIndex);
        }
        break;

      case 'Escape':
        if (this._isOpen) {
          e.preventDefault();
          e.stopPropagation();
          this._close();
          // Restore selected value if any
          if (this._selectedValue) {
            this.inputEl.value = this._selectedLabel;
          }
        }
        break;

      case 'Tab':
        if (this._isOpen && this._highlightIndex >= 0) {
          this._selectIndex(this._highlightIndex);
        }
        this._close();
        break;
    }
  };

  AutoComplete.prototype._filter = function(query) {
    this._filteredItems = filterItems(this._items, query, this.config.maxVisible);
    this._highlightIndex = -1;
    this._renderDropdown(query);
  };

  AutoComplete.prototype._renderDropdown = function(query) {
    if (this._filteredItems.length === 0) {
      this._dropdown.innerHTML = '<div class="ac-no-results">No matches found</div>';
      return;
    }

    var html = '';
    for (var i = 0; i < this._filteredItems.length; i++) {
      var item = this._filteredItems[i];
      var label = typeof item === 'string' ? item : item.label;
      var value = typeof item === 'string' ? item : item.value;
      var highlighted = highlightMatch(label, query);
      var activeClass = i === this._highlightIndex ? ' ac-item-active' : '';

      html += '<div class="ac-item' + activeClass + '" role="option" data-index="' + i + '" data-value="' + escHtml(value) + '">' + highlighted + '</div>';
    }

    this._dropdown.innerHTML = html;

    // Bind click events on items
    var self = this;
    var items = this._dropdown.querySelectorAll('.ac-item');
    for (var j = 0; j < items.length; j++) {
      (function(idx) {
        items[idx].addEventListener('mousedown', function(e) {
          e.preventDefault(); // Prevent blur
          self._selectIndex(idx);
        });
        items[idx].addEventListener('mouseenter', function() {
          self._setHighlight(idx);
        });
      })(j);
    }
  };

  AutoComplete.prototype._moveHighlight = function(direction) {
    var newIndex = this._highlightIndex + direction;
    if (newIndex < 0) newIndex = this._filteredItems.length - 1;
    if (newIndex >= this._filteredItems.length) newIndex = 0;
    this._setHighlight(newIndex);
  };

  AutoComplete.prototype._setHighlight = function(index) {
    // Remove old highlight
    var oldEl = this._dropdown.querySelector('.ac-item-active');
    if (oldEl) oldEl.classList.remove('ac-item-active');

    this._highlightIndex = index;

    // Add new highlight
    var newEl = this._dropdown.querySelector('[data-index="' + index + '"]');
    if (newEl) {
      newEl.classList.add('ac-item-active');
      // Scroll into view
      newEl.scrollIntoView({ block: 'nearest' });
    }
  };

  AutoComplete.prototype._selectIndex = function(index) {
    var item = this._filteredItems[index];
    if (!item) return;

    var value = typeof item === 'string' ? item : item.value;
    var label = typeof item === 'string' ? item : item.label;

    this._selectedValue = value;
    this._selectedLabel = label;
    this.inputEl.value = label;
    this._close();
    this._updateClearButton();

    if (typeof this.config.onSelect === 'function') {
      this.config.onSelect(value, label);
    }
  };

  AutoComplete.prototype._open = function() {
    if (this._disabled) return;
    this._isOpen = true;
    this._dropdown.style.display = 'block';
    this.inputEl.setAttribute('aria-expanded', 'true');
  };

  AutoComplete.prototype._close = function() {
    this._isOpen = false;
    this._dropdown.style.display = 'none';
    this._highlightIndex = -1;
    this.inputEl.setAttribute('aria-expanded', 'false');
  };

  AutoComplete.prototype._updateClearButton = function() {
    this._clearBtn.style.display = (this.inputEl.value || this._selectedValue) ? 'flex' : 'none';
  };

  // ── Public API ──

  AutoComplete.prototype.setItems = function(items) {
    this._items = (items || []).map(function(item) {
      if (typeof item === 'string') {
        return { value: item, label: item };
      }
      return { value: item.value || item.handle || '', label: item.label || item.name || item.text || '' };
    });
    // Sort alphabetically
    this._items.sort(function(a, b) { return a.label.localeCompare(b.label); });
  };

  AutoComplete.prototype.setValue = function(value, silent) {
    var item = this._items.find(function(it) { return it.value === value; });
    if (item) {
      this._selectedValue = item.value;
      this._selectedLabel = item.label;
      this.inputEl.value = item.label;
      this._updateClearButton();
    }
    if (!silent && typeof this.config.onSelect === 'function') {
      this.config.onSelect(value, item ? item.label : value);
    }
  };

  AutoComplete.prototype.getValue = function() {
    return this._selectedValue;
  };

  AutoComplete.prototype.clear = function(silent) {
    var hadValue = this._selectedValue;
    this._selectedValue = null;
    this._selectedLabel = null;
    this.inputEl.value = '';
    this._updateClearButton();
    this._close();

    if (!silent && hadValue && typeof this.config.onSelect === 'function') {
      this.config.onSelect('', '');
    }
  };

  AutoComplete.prototype.enable = function() {
    this._disabled = false;
    this.inputEl.disabled = false;
    this._wrapper.classList.remove('ac-disabled');
  };

  AutoComplete.prototype.disable = function() {
    this._disabled = true;
    this.inputEl.disabled = true;
    this._wrapper.classList.add('ac-disabled');
    this._close();
  };

  AutoComplete.prototype.focus = function() {
    if (!this._disabled) {
      this.inputEl.focus();
    }
  };

  // ── TomSelect-compatible shims (for drop-in replacement) ──

  /**
   * addOption — adds a single option (TomSelect compat)
   * Accepts { value, text } or { value, text, name }
   */
  AutoComplete.prototype.addOption = function(opt) {
    if (!opt || !opt.value) return;
    // Skip placeholder options
    if (opt.value === '') return;
    // Avoid duplicates
    var exists = this._items.some(function(it) { return it.value === opt.value; });
    if (exists) return;
    this._items.push({
      value: opt.value,
      label: opt.text || opt.name || opt.label || opt.value
    });
    // Re-sort
    this._items.sort(function(a, b) { return a.label.localeCompare(b.label); });
  };

  /**
   * clearOptions — removes all options (TomSelect compat)
   */
  AutoComplete.prototype.clearOptions = function() {
    this._items = [];
  };

  /**
   * wrapper property — returns the wrapper element (TomSelect compat)
   */
  Object.defineProperty(AutoComplete.prototype, 'wrapper', {
    get: function() { return this._wrapper; }
  });

  AutoComplete.prototype.destroy = function() {
    // Move input out of wrapper, remove wrapper
    if (this._wrapper && this._wrapper.parentNode) {
      this._wrapper.parentNode.insertBefore(this.inputEl, this._wrapper);
      this._wrapper.parentNode.removeChild(this._wrapper);
    }
    this.inputEl.classList.remove('ac-input');
  };

  // ── Export ──
  AutoComplete.filterItems = filterItems;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = AutoComplete;
  } else {
    root.AutoComplete = AutoComplete;
  }

})(typeof window !== 'undefined' ? window : this);
